/**
 * Per-query React Query persister backed by IndexedDB (Dexie).
 *
 * Opt-in: pass `persister: corpusQueryPersister` in a query's options to have
 * its data survive reloads. Used by the funder workspace search corpus so the
 * workspace can be loaded online and then keep working (including reloads)
 * offline. The persisted entry is restored once on the query's first fetch
 * after mount, then kept up to date after each successful fetch.
 *
 * Cleared on sign-out (see SignOut.tsx) via `deletePersistedQueryCache`.
 */

import type {
  Query,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
} from "@tanstack/react-query";
import { experimental_createQueryPersister } from "@tanstack/query-persist-client-core";
import type { PersistedQuery } from "@tanstack/query-persist-client-core";
import Dexie, { type EntityTable } from "dexie";
import { normalizePersistedCorpusQuery } from "@client/offline/artifactQueryPersistence";

export const QUERY_PERSIST_DB_NAME = "PersistedQueryCache";

/**
 * Drop entries that haven't been read or written for this long. Expiry is
 * idle-based rather than fetch-age-based: the persister's own `maxAge` counts
 * from the last successful fetch, so an immutable entry (e.g. transcript
 * content at `staleTime: Infinity`) would expire on a fixed schedule no
 * matter how actively it's used. Touching on every access means a corpus in
 * regular use never ages out; only abandoned entries are collected.
 */
const MAX_IDLE_MS = 1000 * 60 * 60 * 24 * 30;

type PersistedQueryRow = {
  key: string;
  value: PersistedQuery;
  touchedAt: number;
};

type PersistDb = Dexie & {
  queries: EntityTable<PersistedQueryRow, "key">;
};

let dbInstance: PersistDb | null = null;

function getDb(): PersistDb {
  if (dbInstance == null) {
    const db = new Dexie(QUERY_PERSIST_DB_NAME) as PersistDb;
    db.version(2)
      .stores({ queries: "key, touchedAt" })
      .upgrade((tx) =>
        tx
          .table("queries")
          .toCollection()
          .modify((row: PersistedQueryRow) => {
            row.touchedAt ??= Date.now();
          }),
      );
    dbInstance = db;
    // Collect idle entries once per session, off the critical path.
    void db.queries
      .where("touchedAt")
      .below(Date.now() - MAX_IDLE_MS)
      .delete()
      .catch(() => {});
  }
  return dbInstance;
}

export async function deletePersistedQueryCache(): Promise<void> {
  dbInstance = null;
  await Dexie.delete(QUERY_PERSIST_DB_NAME);
}

/**
 * Approximate size of the persisted query cache, for storage diagnostics.
 * JSON length is a good proxy for what IndexedDB actually stores; walking
 * every row costs ~tens of ms for a few MB, so call this from debug paths
 * only.
 */
export async function estimatePersistedQueryCacheStats(): Promise<{
  entryCount: number;
  approxBytes: number;
}> {
  let entryCount = 0;
  let approxBytes = 0;
  await getDb().queries.each((row) => {
    entryCount += 1;
    approxBytes += JSON.stringify(row.value).length;
  });
  return { entryCount, approxBytes };
}

/**
 * Storage keys are scoped to the effective user id. tRPC query hashes encode
 * only the input, so without this one identity's cached responses could
 * restore for another: an SFC admin's data while impersonating a user whose
 * own request would be denied (e.g. getTrackSummary's role/track gate), or —
 * since a session can change accounts without passing through SignOut (cookie
 * expiry + relogin, direct Auth.js signout, native token swap) — a previous
 * account's round data for the next person on a shared device.
 *
 * Consumers must set the scope (from their auth context) before mounting
 * persisted queries; while it's unknown, persistence is a no-op in both
 * directions, so nothing is ever read from or written to a shared bucket.
 */
let userScope: string | null = null;

export function setPersistedQueryUserScope(effectiveUserId: string | null) {
  userScope = effectiveUserId;
}

function scopedKey(key: string): string | null {
  return userScope == null ? null : `${userScope}|${key}`;
}

// Every storage op is guarded: a rejection out of this adapter reaches
// TanStack's persister wrapper, whose error handler performs ANOTHER storage
// op (removeItem inside the getItem catch) — so with a broken IndexedDB an
// unguarded reject fails the query's real fetch and puts React Query into a
// retry loop. Persistence is a cache; a broken backing degrades to "no
// persistence", never to a broken query.
const persister = experimental_createQueryPersister<PersistedQuery>({
  storage: {
    getItem: async (key) => {
      const fullKey = scopedKey(key);
      if (fullKey == null) return undefined;
      try {
        const row = await getDb().queries.get(fullKey);
        if (row == null) return undefined;
        // Touch so active use keeps the entry alive (fire-and-forget).
        void getDb()
          .queries.update(fullKey, { touchedAt: Date.now() })
          .catch(() => {});
        return normalizePersistedCorpusQuery(row.value);
      } catch {
        return undefined;
      }
    },
    setItem: async (key, value) => {
      const fullKey = scopedKey(key);
      if (fullKey == null) return;
      try {
        await getDb().queries.put({
          key: fullKey,
          value,
          touchedAt: Date.now(),
        });
      } catch {
        // Entry simply isn't persisted; the live query is unaffected.
      }
    },
    removeItem: async (key) => {
      const fullKey = scopedKey(key);
      if (fullKey == null) return;
      try {
        await getDb().queries.delete(fullKey);
      } catch {
        // Stale entry ages out via the idle GC instead.
      }
    },
  },
  // Store the structured value directly — IndexedDB handles objects, so
  // there's no need to JSON-roundtrip megabytes of transcript text.
  serialize: (persistedQuery) => persistedQuery,
  deserialize: (cached) => cached,
  // Expiry is handled by the idle-based GC above (see MAX_IDLE_MS) rather
  // than fetch-age, so an actively used corpus never expires out from under
  // an offline user.
  maxAge: Infinity,
  buster: "v1",
});

/**
 * `persister.persisterFn` is typed against `@tanstack/query-persist-client-core`'s
 * own (exact-pinned) copy of query-core, whose `QueryClient` is nominally
 * incompatible with the app's. The runtime contract is identical, so re-type
 * the function against the app's query-core types.
 */
type CorpusQueryPersisterFn = <T, TQueryKey extends QueryKey>(
  queryFn: QueryFunction<T, TQueryKey, never>,
  context: QueryFunctionContext<TQueryKey>,
  query: Query,
) => Promise<T>;

export const corpusQueryPersister =
  persister.persisterFn as unknown as CorpusQueryPersisterFn;
